import './index.css'
import Intercom from '@disco/api/intercom'

import React from 'react'
import type { ButtonAppearance } from 'components/ui/button'
import Button from 'components/ui/button'
type Props = {
  children?: React.ReactNode
  className?: string
  intercomMsg?: string
  subject?: string
  type?: 'button' | 'link'
  buttonType?: ButtonAppearance
  websiteCheckAvailable?: boolean
  onClick?: () => unknown
  disabled?: boolean
  emailAddress?: string
  dataTestId?: string
}

/**
 * A contact us link component that integrates with Intercom chat or falls back to email
 * @param children - The content to display inside the link or button
 * @param className - Additional CSS classes to apply to the link - defaults to 'ContactUsLink'
 * @param intercomMsg - Optional pre-filled message for Intercom chat
 * @param subject - Optional subject line for email fallback
 * @param type - Whether to render as a 'button' or 'link' - defaults to 'link'
 * @param buttonType - Button appearance type when type is 'button' - defaults to 'primary'
 * @param websiteCheckAvailable - Whether to use website-specific Intercom availability check - defaults to false
 * @param onClick - Optional callback function called when link is clicked
 * @param disabled - Whether the link/button is disabled - defaults to false
 * @param emailAddress - Email address for mailto fallback - defaults to 'support@disco.ac'
 * @param dataTestId - Test ID for automated testing
 * @returns Either a Button component or anchor element depending on type prop
 */
const ContactUsLink = (
  props: Props,
): React.ReactNode | React.ReactElement<'a'> => {
  const openIntercom = (event: React.SyntheticEvent<any>) => {
    if (props.disabled) {
      event.preventDefault()
      return
    }

    if (props.onClick) {
      props.onClick()
    }

    const isAvailable = props.websiteCheckAvailable
      ? Intercom.isAvailableWebsite()
      : Intercom.isAvailable()

    if (!isAvailable) {
      return // ASSUMES: event target has `target` and `href`
      // will open `mailto:` link (native <a> handler)
    }

    event.preventDefault()

    if (props.intercomMsg) {
      Intercom.showNewMessage(props.intercomMsg)
    } else {
      Intercom.showNewMessage()
    }
  }

  let link = `mailto:${props.emailAddress || 'support@disco.ac'}`

  if (props.subject) {
    link += `?subject=${props.subject}`
  }

  if (props.type && props.type === 'button') {
    return (
      <Button
        appearance={props.disabled ? 'subtle' : props.buttonType || 'primary'}
        caption={props.children}
        link={link}
        linkTarget="_blank"
        onClick={openIntercom}
        disabled={props.disabled}
        dataTestId={props.dataTestId}
      />
    )
  } else {
    return (
      <a
        className={props.className ?? 'ContactUsLink'}
        href={link}
        rel="noopener noreferrer"
        target="_blank"
        onClick={openIntercom}
      >
        {props.children}
      </a>
    )
  }
}

export default ContactUsLink
